import React, { useEffect, useState } from "react";
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import Cookies from 'js-cookie';
import { FASTAPI_HOST, IS_MOBILE_APP } from '../settings/config';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Switch from '@mui/material/Switch';
import PLAN_CONFIG from '../settings/planConfig';
import IOSIAPPurchasePage from './IOSIAPPurchasePage';
import { showStyledAlert, showStyledConfirm } from './StyledAlert';

//const userToken = Cookies.get('user_token'); // or whatever your cookie name is
//const partner = Cookies.get('partner'); // or whatever your cookie name is
// Common function to extract error message from any error object (Axios, fetch, etc)
function getErrorMessage(e) {
  let errMsg = "Unknown error";
  if (e?.response?.data) {
    if (typeof e.response.data === 'string') {
      errMsg = e.response.data;
    } else if (e.response.data.detail) {
      errMsg = e.response.data.detail;
    } else if (e.response.data.error) {
      errMsg = e.response.data.error;
    }
  } else if (e?.message) {
    errMsg = e.message;
  }
  return errMsg;
}

function useQuery() {
  return new URLSearchParams(window.location.search);
}

const query = useQuery();
const language = query.get('language');

const Purchase = (props) => {
    // ...existing hooks and state...

    // Open Stripe Customer Portal (must be inside component to access hooks)
    const handleManageBilling = async () => {
      setLoading(true);
      const newWindow = window.open('', '_blank');
      try {
        const res = await axios.post(
          `${FASTAPI_HOST}/api/stripe/create-portal-session`,
          { return_url_base: window.location.origin },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );
        console.log('Stripe portal response:', res.data);
        if (res.data && res.data.url) {
          setBillingAvailable(true);
          newWindow.location = res.data.url;
        } else {
          newWindow.close();
          showStyledAlert(t?.sb_billing_portal_failed || 'Failed to get billing portal link.', { severity: 'error' });
        }
      } catch (e) {
        newWindow.close();
        const errMsg = getErrorMessage(e);
        showStyledAlert((t?.sb_billing_portal_error || 'Error opening billing portal: ') + errMsg, { severity: 'error' });
      }
      setLoading(false);
    };
  // Use prop if provided, else fallback to cookie
  const userToken = Cookies.get('user_token');
  //TODO: parse partner from props later when provided??
  const partner = props.partner || Cookies.get('partner');
  const planData = props.planData;
  //const language = props.language || query.get('language'); 
  const t = props.t;
  const { getToken, signOut } = useAuth();
  const navigate = useNavigate();
  const title = t?.sb_purchase_plans || "Purchase Social Story Plans";
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [showAllSubscriptionHistory, setShowAllSubscriptionHistory] = useState(false);
  const [billingAvailable, setBillingAvailable] = useState(false);
  const [billingSubscriptions, setBillingSubscriptions] = useState([]);
  const [stripePortalAvailable, setStripePortalAvailable] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const refreshUserPlan = async () => {
    if (!userToken) return;
    const res = await axios.get(`${FASTAPI_HOST}/api/get_plan_for_user`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
    const planInfo = res.data.plan_info;
    setUserPlan(planInfo);
    if (planInfo) setBillingAvailable(true);
    if (planInfo?.provider_customer_id) setStripePortalAvailable(true);
  };

  const refreshBillingAvailability = async () => {
    if (!userToken) return;
    const res = await axios.get(`${FASTAPI_HOST}/api/billing/subscription-summary`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
    setBillingAvailable(Boolean(res.data?.subscription_available));
    setBillingSubscriptions(Array.isArray(res.data?.subscriptions) ? res.data.subscriptions : []);
    setStripePortalAvailable(Boolean(res.data?.stripe_portal_available));
  };

  useEffect(() => {
    if (userToken) {
      refreshUserPlan()
        .catch((err) => console.error("Error fetching user plan:", err));
      refreshBillingAvailability()
        .catch((err) => console.error("Error checking billing availability:", err));
    }
  }, [userToken]);
  //console.log("User's current plan:", userPlan);
  //console.log("Scheduled price id:", userPlan?.scheduled_price_id);
  const handleBuy = async (planId) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${FASTAPI_HOST}/api/create_order`,
        { plan_id: planId, partner, return_url_base: window.location.origin },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      if (res.data && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        // After payment, the payment provider should redirect back to our site with a success param
        // We'll add a ?payment_success=1 param to the HomePage URL for demo
        //window.location.href = res.data.checkout_url + `&redirect_url=${encodeURIComponent(window.location.origin + '/?payment_success=1')}`;
      } else {
        // For upgrade/downgrade:
        if (res.data && res.data.message) {
          showStyledAlert(res.data.message, { severity: 'info' });
          
          // Refetch user plan to get updated scheduled_price_id
          const planRes = await axios.get(`${FASTAPI_HOST}/api/get_plan_for_user`, { 
            headers: { Authorization: `Bearer ${userToken}` } 
          });
          console.log("Refetched plan after downgrade:", planRes.data.plan_info);
          setUserPlan(planRes.data.plan_info);
        } else {
          console.warn("Unexpected response from server:", res);
          showStyledAlert(t?.sb_purchase_failed || "Failed to purchase plan. Please try again.", { severity: 'error' });
        }
      }
    } catch (e) {
      const errMsg = getErrorMessage(e);
      showStyledAlert((t?.sb_purchase_error || "Error purchasing plan: ") + errMsg, { severity: 'error' });
    }
    setLoading(false);
  };

  const handleCancelSubscription = async () => {
    let warning = t?.sb_cancel_warning || 'Are you sure you want to cancel your subscription?';
    if (userPlan?.scheduled_price_id) {
      warning = t?.sb_cancel_scheduled_warning || 'You have a scheduled plan change. If you cancel now, your subscription will be cancelled immediately and you will lose access right away. This action cannot be undone.\n\nAre you sure you want to proceed?';
    }
    const confirmed = await showStyledConfirm(warning, {
      confirmLabel: t?.sb_confirm || 'Confirm',
      cancelLabel: t?.sb_cancel || 'Cancel',
      confirmButtonSx: {
        backgroundColor: '#3A6752',
        color: '#ffffff',
        '&:hover': { backgroundColor: '#274D3B' },
      },
    });
    if (!confirmed) {
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${FASTAPI_HOST}/api/cancel_subscription`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      if (res.data && res.data.success) {
        showStyledAlert(t?.sb_cancel_success || 'Subscription cancelled successfully.', { severity: 'success' });
        // Refresh user plan
        const planRes = await axios.get(`${FASTAPI_HOST}/api/get_plan_for_user`, { 
          headers: { Authorization: `Bearer ${userToken}` } 
        });
        setUserPlan(planRes.data.plan_info);
        setManageModalOpen(false);
      } else {
        showStyledAlert(t?.sb_cancel_failed || 'Failed to cancel subscription. Please try again.', { severity: 'error' });
      }
    } catch (e) {
      const errMsg = getErrorMessage(e);
      console.error('Error cancelling subscription:', errMsg);
      showStyledAlert((t?.sb_cancel_error || 'Error cancelling subscription: ') + errMsg, { severity: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!userToken || deletingAccount) return;

    setDeletingAccount(true);
    try {
      const clerkToken = await getToken();
      if (!clerkToken) throw new Error('A current sign-in session is required.');

      await axios.delete(`${FASTAPI_HOST}/api/account`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          'X-Clerk-Token': clerkToken,
        },
      });

      Cookies.remove('user_token');
      setDeleteDialogOpen(false);
      try {
        await signOut();
      } finally {
        navigate('/sign-in', { replace: true });
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      showStyledAlert(
        `${t?.sb_delete_account_error || 'Account deletion failed.'}${detail ? ` ${detail}` : ''}`,
        { severity: 'error' }
      );
      setDeleteDialogOpen(false);
    } finally {
      setDeletingAccount(false);
    }
  };

  // Enhanced: fallback for manual/custom plans
  const getCurrentPlanDetails = () => {
    if (!userPlan) return null;
    const userPlanId = String(userPlan.plan_id);
    for (const plan of PLAN_CONFIG) {

      const displayName = t?.[plan.nameKey] || plan.id|| '';      
      if (plan.id === userPlanId) return { ...plan, displayName: displayName };
      if (plan.monthly && plan.monthly.id === userPlanId) {
        return { ...plan.monthly, displayName: `${displayName} Monthly` };
      }
      if (plan.annual && plan.annual.id === userPlanId) {
        return { ...plan.annual, displayName: `${displayName} Annual` };
      }
    }
  };

  const getScheduledPlanDetails = () => {
    if (!userPlan?.scheduled_price_id) return null;
    const scheduledPriceId = String(userPlan.scheduled_price_id);
    
    // Find the scheduled plan in PLAN_CONFIG
    for (const plan of PLAN_CONFIG) {
      const displayName = t?.[plan.nameKey] || plan.name || '';
      if (plan.id === scheduledPriceId) {
        //console.log("Match found on plan.id");
        return { ...plan, displayName: displayName };
      }
      if (plan.monthly && plan.monthly.id === scheduledPriceId) {
        //console.log("Match found on plan.monthly.id");
        return { ...plan.monthly, displayName: `${displayName} Monthly` };
      }
      if (plan.annual && plan.annual.id === scheduledPriceId) {
        //console.log("Match found on plan.annual.id");
        return { ...plan.annual, displayName: `${displayName} Annual` };
      }
    }
    console.log("No match found for scheduled price ID");
    return null;
  };

  const isMobile = useMediaQuery('(max-width:600px)');
  // Track switch state globally for all plans
  const [isAnnual, setIsAnnual] = useState(false);

  const currentPlan = getCurrentPlanDetails();
  const scheduledPlan = getScheduledPlanDetails();

  // Define isManualPlan to check if the current plan is a manual/custom plan
  const isManualPlan = userPlan && String(userPlan.plan_id).startsWith('manual');
  const activeProvider = String(
    userPlan?.provider || (userPlan?.provider_customer_id ? 'stripe' : (isManualPlan ? 'manual' : ''))
  ).toLowerCase();
  const canCancelInApp = Boolean(userPlan) && !['apple', 'ios', 'google'].includes(activeProvider);
  const hasAppleSubscription = billingSubscriptions.some((subscription) =>
    ['apple', 'ios'].includes(String(subscription?.provider || '').toLowerCase())
  );
  const historicalSubscriptions = userPlan
    ? billingSubscriptions.filter((subscription) => subscription?.status !== 'active')
    : billingSubscriptions;
  const visibleHistoricalSubscriptions = showAllSubscriptionHistory
    ? historicalSubscriptions
    : historicalSubscriptions.slice(0, 1);
  const visibleBillingSubscriptions = showAllSubscriptionHistory
    ? billingSubscriptions
    : billingSubscriptions.slice(0, 1);

  const openManageSubscription = () => {
    setShowAllSubscriptionHistory(false);
    setManageModalOpen(true);
  };

  const closeManageSubscription = () => {
    setShowAllSubscriptionHistory(false);
    setManageModalOpen(false);
  };

  return (
    <Box sx={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: '1rem', color: '#46382F', px: { xs: 1, sm: 4 }, py: { xs: 2, sm: 4 }, width: '100%' }}>
      {IS_MOBILE_APP ? (
        <IOSIAPPurchasePage
          t={t}
          planData={planData}
          userPlan={userPlan}
          userToken={userToken}
          onPlanChanged={refreshUserPlan}
        />
      ) : (
      <>
      <Typography
        variant="subtitle1"
        sx={{
          mt: 1,
          mb: 1,
          color: 'text.primary',
          fontWeight: 800,
          fontSize: { xs: 22, sm: 26 },
          lineHeight: 1.4,
          textAlign: 'center',
        }}
      >
        {title || "Pricing Plans"}
      </Typography>
      <Typography variant="body1" mb={3} align="center">     </Typography>
      
      {/* Top control section: Manage Subscription + Monthly/Annual Toggle */}
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {(userPlan || billingAvailable) && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={openManageSubscription}
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 700,
              borderColor: '#274D3B',
              color: '#274D3B',
              '&:hover': { 
                backgroundColor: '#EDF1DF',
                borderColor: '#1F3D30'
              } 
            }}
          >
            {t?.sb_manage_subscription || 'Manage Subscription'}
          </Button>
        )}
        
        {/* Monthly/Annual Toggle Switch */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" fontWeight={500} sx={{ color: isAnnual ? '#999' : '#222' }}>
            {t?.sb_monthly || 'Monthly'}
          </Typography>
          <Switch
            checked={isAnnual}
            onChange={(e) => setIsAnnual(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#274D3B',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#274D3B',
              },
              '& .MuiSwitch-track': {
                backgroundColor: '#e0e0e0',
              }
            }}
          />
          <Typography variant="body2" fontWeight={500} sx={{ color: isAnnual ? '#222' : '#999' }}>
            {t?.sb_annual || 'Annual'}
          </Typography>
        </Box>
      </Box>
      
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(240px, 1fr))',
          gap: 3,
          alignItems: 'stretch',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        {(planData || PLAN_CONFIG)
          .filter(plan => !String(plan.id).startsWith('manual'))
          .map((plan) => {
          // If plan has monthly/annual, show the appropriate version based on global toggle
          const isSwitchable = plan.monthly && plan.annual;
          const planDetails = isSwitchable
            ? (isAnnual ? plan.annual : plan.monthly)
            : plan;
          // Use translation for plan name
          const displayName = t?.[plan.nameKey] || plan.name || '';
          const displayPrice = planDetails.price;
          // Use translation for features
          const displayFeatures = (planDetails.features || []).map((featureKey) => t?.[featureKey] || featureKey);
          const buyId = planDetails.id;
          const isBasic = plan.id === 'basic';
          const isPremium = plan.id === 'premium';

          // Determine button state and label
          let buttonLabel = t?.sb_buy || 'Buy';
          let buttonDisabled = false;
          let buttonVariant = "outlined";
          let buttonBg = 'transparent';
          let buttonColor = '#274D3B';
          if (userPlan) {
            const userPlanId = String(userPlan.plan_id);
            // Use plan weight for upgrade/buy logic
            const getPlanWeight = (planId) => {
              for (const plan of PLAN_CONFIG) {
                if (plan.monthly && plan.monthly.id === planId) return plan.monthly.weight || 0;
                if (plan.annual && plan.annual.id === planId) return plan.annual.weight || 0;
              }
              return 0;
            };
            const userWeight = getPlanWeight(userPlanId);
            const thisWeight = getPlanWeight(String(buyId));
            if (userPlanId === String(buyId)) {
              buttonLabel = t?.sb_current_plan || 'Current Plan';
              buttonDisabled = true;
              buttonVariant = "contained";
              buttonBg = '#DCE8C8';
              buttonColor = '#1F352A';
            } else if (thisWeight > userWeight) {
              buttonLabel = t?.sb_upgrade || 'Upgrade';
            }
          }
          if (loading) buttonLabel = t?.sb_processing || "Processing...";
          return (
            <Box
              key={plan.id + (isSwitchable ? (isAnnual ? '_annual' : '_monthly') : '')}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                border: isPremium ? '2px solid #BFD2A4' : isBasic ? '1px solid #D6DED2' : '1px solid #E5E1D8',
                boxShadow: isPremium ? '0 12px 32px rgba(111,150,84,.13)' : '0 7px 20px rgba(55,65,58,.06)',
                p: 3,
                borderRadius: 3,
                width: '100%',
                height: '100%',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {isPremium && <Box sx={{ alignSelf: 'flex-end', mb: 1, px: 1.25, py: 0.45, borderRadius: 999, bgcolor: '#E6EED1', color: '#45613A', fontSize: 11, fontWeight: 800, letterSpacing: '.04em' }}>{t?.sb_recommended || 'RECOMMENDED'}</Box>}
              <Typography variant="h6" fontWeight={800} mb={1} color="#46382F">{displayName}</Typography>
              <Typography variant="h5" fontWeight={800} mb={isAnnual ? 0.75 : 2} color="#1F352A">{displayPrice}</Typography>
              {isAnnual && isSwitchable && <Box sx={{ mb: 1.5, px: 1.15, py: 0.4, borderRadius: 999, bgcolor: '#FFF1E5', color: '#8C4E34', fontSize: 11, fontWeight: 800 }}>{t?.sb_annual_saving || 'ANNUAL SAVING'}</Box>}
              <Box component="ul" sx={{ mb: 2, pl: 2.5, width: '100%', flexGrow: 1, '& li': { mb: 0.8 }, '& li::marker': { color: '#6F9654' } }}>
                {displayFeatures.map((feature, i) => (
                  <li key={i} style={{ fontSize: '1rem', fontFamily: 'inherit', marginBottom: 4 }}>{feature}</li>
                ))}
              </Box>

              {buyId !== 'free' ? (
                <Button
                  variant={buttonVariant}
                  color="inherit"
                  fullWidth
                  onClick={() => handleBuy(buyId)}
                  disabled={buttonDisabled || loading}
                  sx={{ mt: 'auto', minHeight: 44, fontSize: '1rem', fontWeight: 800, backgroundColor: buttonBg, color: buttonColor, borderColor: buttonDisabled ? '#AFC48A' : '#274D3B', '&:hover': { backgroundColor: buttonDisabled ? '#DCE8C8' : '#1F3D30', color: buttonDisabled ? '#1F352A' : 'white', borderColor: '#1F3D30' } }}
                >
                  {buttonLabel}
                </Button>
              ) : (
                // Keep the "Free" card the same height as the others by reserving button space
                <Box sx={{ mt: 'auto', width: '100%', height: 40 }} />
              )}
            </Box>
          );
        })}
      </Box>
      </>
      )}

      {IS_MOBILE_APP ? (
        <>
          <Box
            sx={{
              width: '100%',
              maxWidth: 1100,
              mx: 'auto',
              mt: 4,
              pt: 2.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              {t?.sb_account_section || 'Account'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              {t?.sb_delete_account_help || 'Permanently delete your account and all stories associated with it.'}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deletingAccount}
            >
              {deletingAccount
                ? (t?.sb_deleting_account || 'Deleting account...')
                : (t?.sb_delete_account || 'Delete account')}
            </Button>
          </Box>

          <Dialog
            open={deleteDialogOpen}
            onClose={() => !deletingAccount && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{t?.sb_delete_account_title || 'Permanently delete account?'}</DialogTitle>
            <DialogContent>
              <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                {t?.sb_delete_account_warning
                  || 'This cannot be undone. Your account, saved stories, shared story links, role settings, and uploaded media will be deleted.'}
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t?.sb_delete_account_billing
                  || 'Active Stripe billing will be cancelled. App Store subscriptions must be cancelled separately through Apple.'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t?.sb_delete_account_retention
                  || 'Some transaction records may be retained when required by law.'}
              </Typography>
              <Link
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 14 }}
              >
                {t?.sb_manage_apple_subscriptions || 'Manage App Store subscriptions'}
              </Link>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deletingAccount}
              >
                {t?.sb_cancel || 'Cancel'}
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount
                  ? (t?.sb_deleting_account || 'Deleting account...')
                  : (t?.sb_delete_account_confirm || 'Delete permanently')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      {/* Manage Subscription Modal */}
      <Modal
        open={manageModalOpen}
        onClose={closeManageSubscription}
        aria-labelledby="manage-subscription-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : 540,
          bgcolor: '#FDFEFC',
          boxShadow: 24,
          border: '1px solid #C1C7B8',
          borderRadius: 3,
          p: { xs: 2.5, sm: 4 },
          maxHeight: { xs: 'calc(100vh - 32px)', sm: '85vh' },
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
          outline: 'none'
        }}>
          <IconButton
            onClick={closeManageSubscription}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography id="manage-subscription-modal" variant="h5" component="h2" fontWeight={800} color="#2D3913" sx={{ pb: 1.5, mb: 2.5, pr: 4, borderBottom: '1px solid #C1C7B8' }}>
            {t?.sb_manage_subscription || 'Manage Subscription'}
          </Typography>

          {currentPlan && userPlan ? (
            <Box sx={{ '& > ul': { bgcolor: '#F5F8F1', border: '1px solid #C1C7B8', borderRadius: 2, py: 1.5, pr: 2 } }}>
              <Typography variant="h6" fontWeight={500} mb={2}>
                {(t?.sb_current_plan_label || 'Current Plan:') + ' '}{currentPlan.displayName || currentPlan.id}
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={1}>
                {(t?.sb_price_label || 'Price:') + ' '}{currentPlan.monthly?.price || currentPlan.price || (t?.sb_na || 'N/A')}
              </Typography>
              {userPlan?.current_period_end && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  {(t?.sb_period_ends || 'Period Ends:')}{' '}
                  {new Date(userPlan.current_period_end).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
              )}
              <Typography variant="body2" fontWeight={500} mb={1}>
                {t?.sb_features_label || 'Features:'}
              </Typography>
              <Box component="ul" sx={{ mb: 3, pl: 2 }}>
                {(currentPlan.monthly?.features || currentPlan.features || []).length > 0
                  ? (currentPlan.monthly?.features || currentPlan.features || []).map((featureKey, i) => (
                      <li key={i} style={{ fontSize: '0.95rem', marginBottom: 4 }}>{t?.[featureKey] || featureKey}</li>
                    ))
                  : currentPlan.features.map((feature, i) => (
                      <li key={i} style={{ fontSize: '0.95rem', marginBottom: 4 }}>{feature}</li>
                    ))}
              </Box>

              {/* Show scheduled cancellation warning if applicable */}
              {userPlan?.cancel_at_period_end && userPlan?.current_period_end && (
                <Box sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: '#fff3cd',
                  borderRadius: 1,
                  border: '1px solid #ffc107',
                  textAlign: 'center',
                }}>
                  <Typography variant="body1" fontWeight={600} color="#856404" mb={1}>
                    ⚠️ {t?.sb_subscription_cancel_scheduled || 'Subscription Scheduled for Cancellation'}
                  </Typography>
                  <Typography variant="body2" color="#856404">
                    {(t?.sb_subscription_cancel_message || 'Your subscription will end on')}
                    {' '}<strong>{new Date(userPlan.current_period_end).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}</strong>{' '}
                    {(t?.sb_subscription_cancel_period || 'at the end of your billing period.')}
                  </Typography>
                </Box>
              )}
              {/* Show scheduled downgrade information if exists */}
              {scheduledPlan && (
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  bgcolor: '#fff3cd', 
                  borderRadius: 1,
                  border: '1px solid #ffc107'
                }}>
                  <Typography variant="body1" fontWeight={600} color="#856404" mb={1}>
                    ⚠️ {t?.sb_scheduled_plan_change || 'Scheduled Plan Change'}
                  </Typography>
                  <Typography variant="body2" color="#856404" mb={0.5}>
                    {(t?.sb_subscription_change_message || 'Your subscription will change to')}
                    {' '}<strong>{t?.[scheduledPlan.nameKey] || scheduledPlan.displayName}</strong>
                  </Typography>
                  {userPlan?.current_period_end && (
                    <Typography variant="body2" color="#856404">
                      {(t?.sb_effective_label || 'Effective:')}{' '}
                      {new Date(userPlan.current_period_end).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </Typography>
                  )}
                </Box>
              )}

              {historicalSubscriptions.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body1" fontWeight={600} mb={1.5}>
                    {t?.sb_subscription_history || 'Subscription History'}
                  </Typography>
                  {visibleHistoricalSubscriptions.map((subscription, index) => (
                    <Box
                      key={`${subscription.provider || 'provider'}-${subscription.price_id || 'plan'}-${index}`}
                      sx={{ border: '1px solid #C1C7B8', bgcolor: '#F5F8F1', borderRadius: 1.5, p: 1.5, mb: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {(t?.sb_subscription_provider || 'Provider:')} {subscription.provider || (t?.sb_na || 'N/A')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(t?.sb_subscription_status || 'Status:')} {subscription.status || (t?.sb_na || 'N/A')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(t?.sb_current_plan_label || 'Plan:')} {subscription.plan_id || subscription.price_id || (t?.sb_na || 'N/A')}
                      </Typography>
                      {subscription.current_period_end && (
                        <Typography variant="body2" color="text.secondary">
                          {(t?.sb_period_ends || 'Period Ends:')} {new Date(subscription.current_period_end).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {historicalSubscriptions.length > 1 && (
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      onClick={() => setShowAllSubscriptionHistory((show) => !show)}
                      sx={{ mt: 0.5, fontWeight: 600 }}
                    >
                      {showAllSubscriptionHistory
                        ? (t?.sb_show_less || 'Show less')
                        : (t?.sb_show_more || 'Show more')}
                    </Button>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mt: 3 }}>
                {stripePortalAvailable && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth={isMobile}
                    onClick={handleManageBilling}
                    disabled={loading}
                    sx={{
                      fontSize: '1rem',
                      borderColor: '#274D3B',
                      bgcolor: '#274D3B',
                      color: 'white',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: '#1F3D30',
                        borderColor: '#1F3D30'
                      }
                    }}
                  >
                    {loading ? (t?.sb_loading || 'Loading...') : (t?.sb_manage_billing || 'Manage Billing')}
                  </Button>
                )}
                {['apple', 'ios'].includes(activeProvider) && (
                  <Button
                    component="a"
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    color="inherit"
                    fullWidth={isMobile}
                  >
                    {t?.sb_manage_apple_subscriptions || 'Manage App Store subscriptions'}
                  </Button>
                )}
                {canCancelInApp && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    fullWidth={isMobile}
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    sx={{
                      fontSize: '1rem',
                      borderColor: '#B35C5C',
                      color: '#9B3D3D',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: '#FFF1F1',
                        borderColor: '#9B3D3D'
                      }
                    }}
                  >
                    {loading ? (t?.sb_processing || 'Processing...') : (t?.sb_cancel_subscription || 'Cancel Subscription')}
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Box>
              {billingSubscriptions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight={600} mb={1.5}>
                    {t?.sb_subscription_history || 'Subscription History'}
                  </Typography>
                  {visibleBillingSubscriptions.map((subscription, index) => (
                    <Box
                      key={`${subscription.provider || 'provider'}-${subscription.price_id || 'plan'}-${index}`}
                      sx={{ border: '1px solid #C1C7B8', bgcolor: '#F5F8F1', borderRadius: 1.5, p: 1.5, mb: 1 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {(t?.sb_subscription_provider || 'Provider:')} {subscription.provider || (t?.sb_na || 'N/A')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(t?.sb_subscription_status || 'Status:')} {subscription.status || (t?.sb_na || 'N/A')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(t?.sb_current_plan_label || 'Plan:')} {subscription.plan_id || subscription.price_id || (t?.sb_na || 'N/A')}
                      </Typography>
                      {subscription.current_period_end && (
                        <Typography variant="body2" color="text.secondary">
                          {(t?.sb_period_ends || 'Period Ends:')} {new Date(subscription.current_period_end).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {billingSubscriptions.length > 1 && (
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      onClick={() => setShowAllSubscriptionHistory((show) => !show)}
                      sx={{ mt: 0.5, fontWeight: 600 }}
                    >
                      {showAllSubscriptionHistory
                        ? (t?.sb_show_less || 'Show less')
                        : (t?.sb_show_more || 'Show more')}
                    </Button>
                  )}
                </Box>
              )}
              <Typography variant="body1" color="text.secondary" mb={stripePortalAvailable || hasAppleSubscription ? 3 : 0}>
                {billingAvailable
                  ? (t?.sb_no_active_subscription_billing_available || 'No active subscription found. You can still view billing history and invoices.')
                  : (t?.sb_no_active_subscription || 'No active subscription found or billing management unavailable.')}
              </Typography>
              {stripePortalAvailable && (
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth={isMobile}
                  onClick={handleManageBilling}
                  disabled={loading}
                  sx={{
                    fontSize: '1rem',
                    borderColor: '#274D3B',
                    bgcolor: '#274D3B',
                    color: 'white',
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: '#1F3D30',
                      borderColor: '#1F3D30'
                    }
                  }}
                >
                  {loading ? (t?.sb_loading || 'Loading...') : (t?.sb_manage_billing || 'Manage Billing')}
                </Button>
              )}
              {hasAppleSubscription && (
                <Button
                  component="a"
                  href="https://apps.apple.com/account/subscriptions"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="inherit"
                  fullWidth={isMobile}
                >
                  {t?.sb_manage_apple_subscriptions || 'Manage App Store subscriptions'}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Purchase;
